import React, { type ChangeEvent, useEffect, useState } from 'react';
import { style } from 'typestyle';
import * as styles from './input.styles';

interface Props {
  readonly type?: string;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly defaultValue?: string;
  readonly value?: string;
  readonly maxLength?: number;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly onChange?: (value: string) => void;
  readonly onFocusChange?: (focus: boolean, value: string) => void;
  readonly onEnter?: () => void;
  readonly doClearOnEnter?: boolean;
}

const Input = ({
  type,
  disabled,
  placeholder,
  defaultValue,
  value,
  maxLength,
  min,
  max,
  step,
  onChange,
  onFocusChange,
  onEnter,
  doClearOnEnter,
}: Props): React.JSX.Element => {
  const [inputvalue, updateInputValue] = useState(value ?? '');

  useEffect(() => {
    updateInputValue(value ?? '');
  }, [value]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    updateInputValue(event.target.value);
    onChange?.(event.target.value);
  };

  const onFocus = (event: ChangeEvent<HTMLInputElement>): void => onFocusChange?.(true, event.target.value);

  const onBlur = (event: ChangeEvent<HTMLInputElement>): void => onFocusChange?.(false, event.target.value);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      onEnter?.();
      if (doClearOnEnter) {
        updateInputValue('');
      }
    }
  };

  return (
    <input
      className={style(styles.input(disabled))}
      type={type}
      disabled={disabled}
      placeholder={placeholder}
      defaultValue={defaultValue}
      value={inputvalue}
      onChange={onInputChange}
      onFocus={onFocus}
      onBlur={onBlur}
      maxLength={maxLength}
      min={min}
      max={max}
      step={step}
      onKeyPress={handleKeyPress}
    />
  );
};

export default Input;
